import { formatCount, formatPercent } from './format';
import type { TileTone } from './StatTile';

/**
 * A hand-rolled SVG donut — CLM-81 (D-81-1, D-81-7). One `<circle>` per
 * non-zero segment drawn with `stroke-dasharray` on a fixed circumference
 * (r = 56), coloured through the same tone classes as the tiles (tokens only);
 * the centre `<text>` is `formatPercent(centre)` — "—" when `null` — with the
 * caption underneath. The legend is a `<ul>` of `.tag` chips with counts.
 *
 * CLM-95 (D-95-1) — three additive props turn each legend chip into a toggle
 * button (`aria-expanded`/`aria-controls`, the same disclosure pattern as
 * `StatTile`) instead of a static `<span>`. Omitting `onSegmentToggle` leaves
 * every other consumer of this component untouched; `Donut` has exactly one
 * caller today (`AnalyticsDashboard.tsx`) so this is additive in practice too,
 * not just in principle.
 */

export interface DonutSegment {
  readonly key: string;
  readonly label: string;
  readonly n: number;
  readonly tone: TileTone;
}

const R = 56;
const STROKE = 18;
const SIZE = 160;
const CIRCUMFERENCE = 2 * Math.PI * R;

interface DonutBaseProps {
  readonly title: string;
  readonly segments: readonly DonutSegment[];
  /** 0…1 or `null` when there is nothing in scope. */
  readonly centre: number | null;
  readonly caption: string;
}

/**
 * `onSegmentToggle`, `panelId` and `segmentTriggerId` are a single mode, not
 * three independent optionals (the same discriminated-union shape as
 * `StatTile`'s `onToggle`/`expanded`/`panelId`/`id`, D-95-3 finding #3/#4): a
 * caller that supplies `onSegmentToggle` without the other two is a TS error
 * instead of a legend button with an `undefined` `aria-controls` and no id
 * for `collapsePanel()` to focus back to.
 */
type DonutProps =
  | (DonutBaseProps & {
      readonly onSegmentToggle?: undefined;
      readonly activeSegment?: undefined;
      readonly panelId?: undefined;
      readonly segmentTriggerId?: undefined;
    })
  | (DonutBaseProps & {
      readonly onSegmentToggle: (key: string) => void;
      readonly activeSegment?: string | undefined;
      readonly panelId: string;
      /** A stable id per segment's button, so a disclosure's collapse control can focus back to it (D-95-3). */
      readonly segmentTriggerId: (key: string) => string;
    });

export function Donut(props: DonutProps) {
  const { title, segments, centre, caption } = props;
  const total = segments.reduce((acc, s) => acc + s.n, 0);
  let offset = 0;
  const drawn = segments
    .filter((s) => s.n > 0)
    .map((s) => {
      const length = total === 0 ? 0 : (s.n / total) * CIRCUMFERENCE;
      const dash = `${String(length)} ${String(CIRCUMFERENCE - length)}`;
      const dashOffset = -offset;
      offset += length;
      return { ...s, dash, dashOffset };
    });

  return (
    <div className="chart donut">
      <svg role="img" aria-label={title} viewBox={`0 0 ${String(SIZE)} ${String(SIZE)}`} width={SIZE} height={SIZE}>
        <circle cx={SIZE / 2} cy={SIZE / 2} r={R} className="track" strokeWidth={STROKE} fill="none" />
        {drawn.map((s) => (
          <circle
            key={s.key}
            data-segment={s.key}
            className={`segment tone-${s.tone}`}
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            fill="none"
            strokeWidth={STROKE}
            strokeDasharray={s.dash}
            strokeDashoffset={s.dashOffset}
            transform={`rotate(-90 ${String(SIZE / 2)} ${String(SIZE / 2)})`}
          />
        ))}
        <text x={SIZE / 2} y={SIZE / 2 - 2} textAnchor="middle" className="centre">
          {formatPercent(centre)}
        </text>
        <text x={SIZE / 2} y={SIZE / 2 + 16} textAnchor="middle" className="caption">
          {caption}
        </text>
      </svg>
      <ul className="legend" aria-label={`${title} legend`}>
        {segments.map((s) => (
          <li key={s.key}>
            {props.onSegmentToggle === undefined ? (
              <span className={`tag ${s.tone}`}>
                {s.label} · {formatCount(s.n)}
              </span>
            ) : (
              <button
                type="button"
                id={props.segmentTriggerId(s.key)}
                className={`tag ${s.tone} legend-toggle`}
                aria-expanded={props.activeSegment === s.key}
                aria-controls={props.panelId}
                onClick={() => {
                  props.onSegmentToggle(s.key);
                }}
              >
                {s.label} · {formatCount(s.n)}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
